// <purpose>Dashboard hero: funnel-as-filter + Treemap + zero-rail, Active↔Neuanalysen flip (Morph+), light/dark palette.</purpose>
// <dependencies>useCategoryMeta, usePaletteMode, distributionStats, Treemap, ModeSwapIcon, theme.colors.mode.</dependencies>
// <constraints>Funnel windows match the active time pill (server-pre-computed); box click scopes only the treemap. Fixed height; rail pre-allocated. Morph+ timing shared with StatistikSeite.</constraints>
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { theme } from '../theme';
import { useCategoryMeta } from '../context/ClassificationSchemaContext';
import { usePaletteMode } from '../context/PaletteModeContext';
import { Treemap, TreemapTile } from './Treemap';
import { ModeSwapIcon, CategoryVocabularyMissingNotice, SkeletonBlock, ServedFace } from './shared';
import type { ActiveDistribution } from '../types';
import {
  FrequencyBucket, TimeFilter,
  funnelActive, funnelRejections, categoryVolume, neuanalyse,
} from '../services/distributionStats';
import type { ActiveBucket } from '../services/lifecycleBuckets';

const TIME_PILLS: { label: string; value: TimeFilter }[] = [
  { label: '7T', value: '7d' }, { label: '30T', value: '30d' },
  { label: 'Wo.', value: 'thisWeek' }, { label: 'Mo.', value: 'thisMonth' },
];
const TREEMAP_H = 125;
const TREEMAP_W_FALLBACK = 600;
const RECOLOR = 'background-color 0.58s cubic-bezier(0.4,0,0.2,1)'; // Morph+ recolor

const EMPTY_RECORD = (keys: string[]) => Object.fromEntries(keys.map(k => [k, 0]));

export const DistributionHero: React.FC<{ distribution: ActiveDistribution | null }> = ({ distribution }) => {
  const cm = useCategoryMeta();
  const { mode: palette } = usePaletteMode();

  const [lifecycle, setLifecycle] = useState<ActiveBucket | null>(null);
  const [frequency, setFrequency] = useState<FrequencyBucket | null>(null);
  const [timeFilter, setTimeFilter] = useState<TimeFilter | null>(null);

  const [mode, setMode] = useState<'active' | 'rejections'>('active');
  const [displayMode, setDisplayMode] = useState<'active' | 'rejections'>('active');
  const [swapping, setSwapping] = useState(false);
  useEffect(() => {
    if (displayMode === mode) { if (swapping) setSwapping(false); return; }
    setSwapping(true);
    const id = setTimeout(() => { setDisplayMode(mode); setSwapping(false); }, 200); // Morph+ cross-fade dip
    return () => clearTimeout(id);
  }, [mode, displayMode, swapping]);
  const flip = () => { setMode(p => (p === 'active' ? 'rejections' : 'active')); setLifecycle(null); setFrequency(null); };

  const isRej = displayMode === 'rejections';
  const m = isRej ? theme.colors.mode.rejections[palette] : theme.colors.mode.active;

  // Callback ref so the ResizeObserver attaches whenever the real body mounts —
  // a mount-only useEffect misses it when the null-skeleton renders first (bodyRef.current is null on mount).
  const roRef = useRef<ResizeObserver | null>(null);
  const [width, setWidth] = useState(0);
  const bodyRef = useCallback((el: HTMLDivElement | null) => {
    roRef.current?.disconnect();
    roRef.current = null;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(entries => setWidth(entries[0].contentRect.width));
    ro.observe(el);
    roRef.current = ro;
  }, []);
  const w = width || TREEMAP_W_FALLBACK;

  // Task C loading posture. cm.configLoading is TRUE only during the genuine first /config
  // fetch of a session — categoryNounCompound and every other vocab-derived accessor are
  // untrustworthy while it's true (they'd fall through to the raw `{category.…}` placeholder),
  // so the whole card renders a shimmer instead of any of them. Distinct from `!distribution`
  // below: a slow /distribution fetch with vocab already resolved still shows the real (dimmed)
  // noun text, unchanged from before this task.
  const showSkeleton = !distribution || cm.configLoading;

  // All hooks must run unconditionally (Rules of Hooks). Null distribution yields zeros.
  const funnel = useMemo(() =>
    distribution ? funnelActive(distribution, timeFilter) : { uploads: 0, offen: 0, entschieden: 0 },
    [distribution, timeFilter]);
  const rejFunnel = useMemo(() =>
    distribution ? funnelRejections(distribution, timeFilter) : { one: 0, two: 0, threePlus: 0 },
    [distribution, timeFilter]);
  // performanceCategoryKeys, not categoryKeys: this card measures how the classifier spread the
  // work across real categories — an unclassified value ("could not be classified") is not one of them, and
  // a tile for it would read as a category with a suspiciously large share. Identical to
  // categoryKeys everywhere except a served vocabulary that marks a value.
  const volume = useMemo(() =>
    distribution ? categoryVolume(distribution, cm.performanceCategoryKeys, { lifecycle, timeFilter }) : EMPTY_RECORD(cm.performanceCategoryKeys),
    [distribution, cm.performanceCategoryKeys, lifecycle, timeFilter]);
  const neu = useMemo(() =>
    distribution ? neuanalyse(distribution, cm.performanceCategoryKeys, { frequency, timeFilter }) : { volume: EMPTY_RECORD(cm.performanceCategoryKeys), count: EMPTY_RECORD(cm.performanceCategoryKeys), rate: EMPTY_RECORD(cm.performanceCategoryKeys) },
    [distribution, cm.performanceCategoryKeys, frequency, timeFilter]);

  const tiles: TreemapTile[] = useMemo(() => isRej
    ? cm.performanceCategoryKeys.filter(k => neu.volume[k] > 0).map(k => ({
        key: k, abbr: cm.abbr(k), label: cm.label(k), value: neu.volume[k], displayValue: neu.count[k],
        ariaLabel: `${cm.label(k)} (${cm.abbr(k)}): ${neu.count[k]} Neuanalysen`, fillRate: neu.rate[k],
      }))
    : cm.performanceCategoryKeys.filter(k => volume[k] > 0).map(k => ({
        key: k, abbr: cm.abbr(k), label: cm.label(k), value: volume[k], displayValue: volume[k],
        ariaLabel: `${cm.label(k)} (${cm.abbr(k)}): ${volume[k]} Vorgänge`,
      })),
    [isRej, volume, neu, cm]);
  const zeroKeys = useMemo(() => cm.performanceCategoryKeys.filter(k => (isRej ? neu.volume[k] : volume[k]) === 0), [isRej, volume, neu, cm]);

  // Skeleton: pre-allocates the exact same footprint as the real card below. THE reconciled
  // invariant (global `* { box-sizing: border-box }`, App.css): TREEMAP_H + 32 is always the
  // CONTENT height handed to whatever sits INSIDE the sm/md-padded body wrapper below — never a
  // height set directly on the padded wrapper itself, which under border-box would swallow the
  // padding into that same number and undershoot the real footprint by sm+md (~24px). All three
  // body-wrapper contents (this skeleton's SkeletonBlock, CategoryVocabularyMissingNotice's
  // minHeight, and the loaded Treemap+zero-rail stack) size to TREEMAP_H + 32 under this one rule,
  // so none of the three swaps ever shifts the card's height.
  if (showSkeleton) {
    // The funnel labels ARE vocab-derived now (Task R2, part 4c, 2026-09-27) — ServedFace itself
    // decides shimmer-vs-word per cm.configLoading, so this branch (driven by `!distribution`, a
    // DIFFERENT condition) renders the real served word the moment vocabulary has resolved even
    // while still waiting on the distribution fetch, exactly like the loaded card below.
    return (
      <div style={{ marginBottom: theme.spacing.lg, fontFamily: theme.font.family }}>
        {/* Funnel row placeholder — pre-allocates same height as boxes */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: theme.spacing.md, marginBottom: theme.spacing.lg }}>
          {(['uploads', 'offen', 'entschieden'] as ActiveBucket[]).map((key, i) => (
            <React.Fragment key={key}>
              {i > 0 && <span style={{ fontSize: 18, color: theme.colors.textSecondary, userSelect: 'none', flexShrink: 0 }}>&rarr;</span>}
              <div style={{
                flex: '1 1 0', maxWidth: 160, minWidth: 80,
                backgroundColor: m.card, borderRadius: theme.borderRadius.md,
                padding: `${theme.spacing.sm}px ${theme.spacing.md}px`, textAlign: 'center',
                border: '2px solid transparent',
                transition: RECOLOR,
              }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: m.chrome, opacity: 0.85 }}>
                  <ServedFace family="bucket" faceKey={key} nullAs="key" width={70} height={17} render={w => w} />
                </div>
                <div style={{ fontSize: 28, fontWeight: 700, color: m.bigText, lineHeight: 1.2, opacity: 0.25 }}>—</div>
              </div>
            </React.Fragment>
          ))}
        </div>
        <div data-card style={{ background: m.card, borderRadius: theme.borderRadius.md, boxShadow: theme.shadows.card, overflow: 'hidden', transition: RECOLOR }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: `${theme.spacing.sm}px ${theme.spacing.md}px` }}>
            {/* Vocab-derived noun ("Marktprozessverteilung"/"Kategorieverteilung"/…) has no
                trustworthy value yet while cm.configLoading is true — shimmer, never the
                `{category.noun}` placeholder floor. A distribution-only load (vocab already
                resolved) instead shows the real, dimmed title exactly as before this task. */}
            {cm.configLoading
              ? <SkeletonBlock width={150} height={14} />
              : <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: m.chrome, opacity: 0.4 }}>{cm.categoryNounCompound}verteilung</span>}
          </div>
          <div style={{ padding: `${theme.spacing.sm}px ${theme.spacing.md}px ${theme.spacing.md}px` }}>
            <SkeletonBlock height={TREEMAP_H + 32} />
          </div>
        </div>
      </div>
    );
  }

  // The accessible name (aria-label, a plain string) can't carry the ServedFace element the visible
  // `label` does — same three-state reading, spelled once here. While the vocabulary is still
  // loading, cm.face cannot be trusted (it would answer the raw key) — like every native `title=`
  // tooltip elsewhere in this task, the honest answer is no accessible name at all rather than a
  // raw key reaching assistive tech (fix round 1); this branch is unreachable today (showSkeleton
  // already covers cm.configLoading before boxes exist) but the fallback is spelled explicitly so
  // a future decoupling of the two conditions can't reopen the leak silently.
  const bucketWord = (key: ActiveBucket): string | undefined => cm.configLoading ? undefined : (cm.face('bucket', key) ?? key);

  const boxes: { key: string; label: React.ReactNode; ariaLabel: string | undefined; value: number; fail: boolean }[] = isRej
    ? [{ key: 'one', label: '1×', ariaLabel: '1×', value: rejFunnel.one, fail: false }, { key: 'two', label: '2×', ariaLabel: '2×', value: rejFunnel.two, fail: false }, { key: 'threePlus', label: '≥3×', ariaLabel: '≥3×', value: rejFunnel.threePlus, fail: true }]
    : (['uploads', 'offen', 'entschieden'] as const).map(key => ({
        key,
        label: <ServedFace family="bucket" faceKey={key} nullAs="key" width={70} height={17} render={w => w} />,
        ariaLabel: bucketWord(key),
        value: funnel[key],
        fail: false,
      }));
  const selectedKey: string | null = isRej ? frequency : lifecycle;
  const onBox = (key: string) => isRej ? setFrequency(f => (f === key ? null : (key as FrequencyBucket))) : setLifecycle(l => (l === key ? null : (key as ActiveBucket)));

  const fadeStyle = { opacity: swapping ? 0 : 1, transition: 'opacity 0.2s ease' } as const;

  return (
    <div style={{ marginBottom: theme.spacing.lg, fontFamily: theme.font.family }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: theme.spacing.md, marginBottom: theme.spacing.lg }}>
        {boxes.map((b, i) => {
          const selected = selectedKey === b.key;
          return (
            <React.Fragment key={b.key}>
              {i > 0 && <span style={{ fontSize: 18, color: theme.colors.textSecondary, userSelect: 'none', flexShrink: 0 }}>&rarr;</span>}
              <button onClick={() => onBox(b.key)} aria-pressed={selected} title={b.fail ? 'Smarttix kommt nicht weiter' : undefined}
                aria-label={b.ariaLabel}
                style={{
                  flex: '1 1 0', maxWidth: 160, minWidth: 80, cursor: 'pointer',
                  backgroundColor: m.card, borderRadius: theme.borderRadius.md,
                  padding: `${theme.spacing.sm}px ${theme.spacing.md}px`, textAlign: 'center',
                  border: `2px solid ${selected ? m.card : 'transparent'}`,
                  boxShadow: selected ? `0 0 0 3px ${m.chrome}24` : 'none',
                  outline: 'none', transition: RECOLOR,
                }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: m.chrome, opacity: 0.85 }}>{b.label}</div>
                <div style={{ fontSize: 28, fontWeight: 700, color: m.bigText, lineHeight: 1.2, ...fadeStyle }}>{b.value.toLocaleString('de-DE')}</div>
              </button>
            </React.Fragment>
          );
        })}
      </div>

      <div data-card style={{ background: m.card, borderRadius: theme.borderRadius.md, boxShadow: theme.shadows.card, overflow: 'hidden', transition: RECOLOR }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: `${theme.spacing.sm}px ${theme.spacing.md}px` }}>
          <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: m.chrome, ...fadeStyle }}>
            {isRej ? `Neuanalysen je ${cm.categoryNoun}` : `${cm.categoryNounCompound}verteilung`}
          </span>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            {TIME_PILLS.map(p => {
              const on = timeFilter === p.value;
              return (
                <button key={p.value} onClick={() => setTimeFilter(on ? null : p.value)} aria-pressed={on}
                  style={{
                    padding: '2px 8px', fontSize: 10, fontWeight: on ? 600 : 400, cursor: 'pointer',
                    color: on ? m.pillOnText : m.chrome, backgroundColor: on ? m.pillOnBg : `${m.chrome}14`,
                    border: `1px solid ${on ? m.pillOnBg : m.pillOffBorder}`, borderRadius: 20, whiteSpace: 'nowrap', outline: 'none',
                    transition: RECOLOR,
                  }}>{p.label}</button>
              );
            })}
            <button onClick={flip} aria-pressed={isRej}
              aria-label={isRej ? 'Verteilung anzeigen — Neuanalysen/Verteilung umschalten' : 'Neuanalysen anzeigen — Neuanalysen/Verteilung umschalten'}
              title={isRej ? 'Verteilung anzeigen' : 'Neuanalysen anzeigen'}
              style={{
                width: 24, height: 24, borderRadius: '50%', cursor: 'pointer',
                background: `${m.chrome}24`, border: `1px solid ${m.pillOffBorder}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: m.chrome, outline: 'none',
                transition: 'transform 0.72s cubic-bezier(0.34,1.45,0.5,1), background 0.4s', transform: isRej ? 'rotate(180deg)' : 'none',
              }}>
              <ModeSwapIcon size={13} color={m.chrome} />
            </button>
          </div>
        </div>
        <div ref={bodyRef} style={{ padding: `${theme.spacing.sm}px ${theme.spacing.md}px ${theme.spacing.md}px` }}>
          {/* Role-configuration gap (no schema/vocabulary for a KNOWN non-legacy role) takes
              precedence over the normal Treemap+zero-rail — performanceCategoryKeys is [] here for a
              role-config reason, not because every category genuinely has zero tickets, and an
              empty Treemap with an empty zero-rail (no "Ohne Vorgänge" text either) would render
              completely blank, more silent than even a generic "Keine Daten". Exactly the normal
              body's footprint (Treemap TREEMAP_H + zero-rail's 8px marginTop + its own 24px
              minHeight = TREEMAP_H+32 — the SAME content height the skeleton above hands its
              SkeletonBlock, per the reconciled invariant at this component's skeleton branch)
              so swapping this in never shifts the card's height. */}
          {cm.vocabularyMissing ? (
            <CategoryVocabularyMissingNotice minHeight={TREEMAP_H + 32} />
          ) : (
            <>
              <Treemap tiles={tiles} width={w} height={TREEMAP_H} tileColor={m.tile} textColor={m.tileText} fillColor={isRej ? (m.fill ?? undefined) : undefined} />
              <div data-testid="zero-rail" style={{ display: 'flex', alignItems: 'center', gap: 6, minHeight: 24, marginTop: 8 }}>
                {zeroKeys.length > 0 && <span style={{ fontSize: 10, color: m.chrome, opacity: 0.85 }}>Ohne Vorgänge:</span>}
                {zeroKeys.map(k => (
                  <span key={k} title={cm.label(k)} style={{ fontSize: 10, fontWeight: 700, color: m.tileText, background: m.tile, borderRadius: theme.borderRadius.sm, padding: '2px 6px', transition: RECOLOR }}>{cm.abbr(k)}</span>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
